import { useCallback, useEffect, useState } from "react";
import { petsApi, sharingApi } from "../services/api";
import ModuleAskBar from "../components/shared/ModuleAskBar";
import SheetHandle from "../components/shared/SheetHandle";
import ConfirmModal from "../components/shared/ConfirmModal";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import "./PetsPage.css";

const EMPTY_PET = {
  name: "",
  species: "dog",
  breed: "",
  sex: "",
  birth_date: "",
  adoption_date: "",
  weight_lbs: "",
  microchip_number: "",
  feeding_notes: "",
  allergy_notes: "",
  caregiver_notes: "",
  medical_conditions: "",
  medication_notes: "",
  primary_vet_name: "",
  primary_vet_phone: "",
  emergency_vet_name: "",
  emergency_vet_phone: "",
  insurance_provider: "",
  insurance_policy_number: "",
  photo_url: "",
};
const EMPTY_CARE = {
  pet_id: "",
  title: "",
  care_type: "preventive",
  next_due: "",
  frequency_days: "365",
  estimated_cost: "",
  provider_id: "",
  vault_item_id: "",
  instructions: "",
  dosage: "",
};
const EMPTY_ROUTINE = {
  pet_id: "",
  title: "",
  routine_type: "feeding",
  time_of_day: "morning",
  scheduled_time: "",
  duration_minutes: "",
  distance: "",
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  dosage: "",
  instructions: "",
  remaining_quantity: "",
  dose_quantity: "1",
  supply_unit: "doses",
  refill_threshold: "5",
  reminder_enabled: false,
  reminder_time: "",
  pet_food_supply_id: "",
  consumption_quantity: "",
  low_stock_threshold: "",
};
const EMPTY_PET_FOOD = { pet_id: "", name: "", quantity: "", unit: "cups", expiry_date: "", low_stock_threshold: "" };
const EMPTY_HEALTH = {
  entry_type: "weight",
  entry_date: new Date().toLocaleDateString("en-CA"),
  value_numeric: "",
  unit: "lb",
  severity: "",
  notes: "",
};
const EMPTY_CLAIM = { pet_id: "", title: "", claim_date: new Date().toLocaleDateString("en-CA"), claim_number: "", status: "draft", amount: "", reimbursement_amount: "", vault_item_id: "", notes: "" };
const EMPTY_LOST = { contact_name: "", contact_phone: "", last_seen_date: new Date().toLocaleDateString("en-CA"), last_seen_location: "", notes: "" };
const ROUTINE_LABEL = {
  feeding: "🍽️ Feeding", medication: "💊 Medication", walk: "🦮 Walk",
  exercise: "🎾 Exercise", water: "💧 Water", bathroom: "🐾 Bathroom",
  grooming: "🪮 Grooming", other: "✓ Other",
};
const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const CARE_LABEL = {
  feeding: "Feeding",
  medication: "Medication",
  vaccination: "Vaccination",
  vet: "Veterinary",
  grooming: "Grooming",
  preventive: "Preventive",
  insurance: "Insurance",
  other: "Other",
};
const petIcon = (species) =>
  ({
    dog: "🐕",
    cat: "🐈",
    bird: "🐦",
    fish: "🐟",
    rabbit: "🐇",
    reptile: "🦎",
  })[String(species).toLowerCase()] || "🐾";
const compressPetPhoto = (file) => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith("image/")) return reject(new Error("Choose an image file."));
  const reader = new FileReader();
  reader.onload = () => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, 512 / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext("2d");
      if (!context) return reject(new Error("Could not process this image."));
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    image.onerror = () => reject(new Error("Could not read this image."));
    image.src = reader.result;
  };
  reader.onerror = () => reject(new Error("Could not load this file."));
  reader.readAsDataURL(file);
});
const WeightTrend = ({ points = [] }) => {
  if (points.length < 2) return <div className="hm-pet-trend-empty">Add at least two weight entries to see a trend.</div>;
  const values = points.map((point) => point.value), min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  return <div className="hm-pet-weight-trend" aria-label={`Weight history from ${values[0]} to ${values.at(-1)} pounds`}>{points.map((point) => <div key={`${point.date}-${point.value}`}><i style={{ height: `${24 + ((point.value - min) / span) * 56}%` }} /><small>{new Date(`${point.date}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</small><b>{point.value}</b></div>)}</div>;
};
const BREED_SUGGESTIONS = {
  dog: [
    "Labrador Retriever",
    "Golden Retriever",
    "German Shepherd",
    "French Bulldog",
    "Poodle",
    "Bulldog",
    "Beagle",
    "Rottweiler",
    "Dachshund",
    "Yorkshire Terrier",
    "Boxer",
    "Australian Shepherd",
    "Cavalier King Charles Spaniel",
    "Chihuahua",
    "Shih Tzu",
    "Border Collie",
    "Mixed breed",
  ],
  cat: [
    "Domestic Shorthair",
    "Domestic Longhair",
    "Maine Coon",
    "Siamese",
    "Persian",
    "Ragdoll",
    "British Shorthair",
    "Bengal",
    "Sphynx",
    "Abyssinian",
    "Russian Blue",
    "Mixed breed",
  ],
  bird: [
    "Budgerigar (Budgie)",
    "Cockatiel",
    "Parakeet",
    "Canary",
    "Lovebird",
    "African Grey Parrot",
    "Cockatoo",
    "Finch",
    "Macaw",
  ],
  fish: ["Betta", "Goldfish", "Guppy", "Tetra", "Koi", "Angelfish", "Molly"],
  rabbit: [
    "Holland Lop",
    "Netherland Dwarf",
    "Mini Rex",
    "Flemish Giant",
    "Lionhead",
    "Dutch",
    "Mixed breed",
  ],
  reptile: [
    "Bearded Dragon",
    "Leopard Gecko",
    "Ball Python",
    "Corn Snake",
    "Red-Eared Slider",
    "Blue-Tongued Skink",
    "Chameleon",
  ],
};
const ALLERGY_SUGGESTIONS = [
  "None known",
  "Chicken",
  "Beef",
  "Dairy",
  "Grain/wheat",
  "Fish",
  "Egg",
  "Flea saliva",
  "Pollen/environmental",
  "Dust mites",
  "Certain flea/tick medications",
];
const FEEDING_ROUTINE_SUGGESTIONS = [
  "1 cup dry kibble, twice daily",
  "1/2 cup dry kibble, twice daily",
  "2 cups dry kibble, twice daily",
  "1 can wet food, once daily",
  "Free-fed dry kibble",
  "Raw diet, twice daily",
  "Prescription diet, per vet instructions",
];
const PET_FOOD_SUGGESTIONS = [
  "Dry dog food", "Wet dog food", "Prescription dog food", "Dry cat food",
  "Wet cat food", "Prescription cat food", "Small animal food", "Bird food",
  "Fish food", "Pet treats",
];
const CARE_TITLE_SUGGESTIONS = {
  vet: ["Annual wellness exam", "Dental exam", "Follow-up visit"],
  vaccination: ["Rabies booster", "Core vaccination"],
  preventive: [
    "Heartworm & flea/tick prevention",
    "Flea & parasite prevention",
  ],
  grooming: ["Grooming appointment", "Nail trim"],
  medication: ["Daily medication", "Prescription refill"],
  insurance: ["Pet insurance renewal"],
  feeding: ["Review feeding plan"],
};

export default function PetsPage() {
  const [data, setData] = useState(null),
    [pet, setPet] = useState(EMPTY_PET),
    [editingPet, setEditingPet] = useState(null),
    [petSheet, setPetSheet] = useState(false),
    [care, setCare] = useState(EMPTY_CARE),
    [careSheet, setCareSheet] = useState(false),
    [editingCare, setEditingCare] = useState(null),
    [routine, setRoutine] = useState(EMPTY_ROUTINE),
    [routineSheet, setRoutineSheet] = useState(false),
    [editingRoutine, setEditingRoutine] = useState(null),
    [routineTimes, setRoutineTimes] = useState([{ time_of_day: "morning", scheduled_time: "" }]),
    [petFood, setPetFood] = useState(EMPTY_PET_FOOD),
    [petFoodSheet, setPetFoodSheet] = useState(false),
    [editingPetFood, setEditingPetFood] = useState(null),
    [healthPet, setHealthPet] = useState(null),
    [healthEntry, setHealthEntry] = useState(EMPTY_HEALTH),
    [healthRange, setHealthRange] = useState("90"),
    [healthType, setHealthType] = useState("all"),
    [caregiverBrief, setCaregiverBrief] = useState(null),
    [vetVisitBrief, setVetVisitBrief] = useState(null),
    [claim, setClaim] = useState(EMPTY_CLAIM),
    [claimSheet, setClaimSheet] = useState(false),
    [editingClaim, setEditingClaim] = useState(null),
    [lostPet, setLostPet] = useState(null),
    [lostDetails, setLostDetails] = useState(EMPTY_LOST),
    [lostPhotoFile, setLostPhotoFile] = useState(null),
    [lostPhotoUrl, setLostPhotoUrl] = useState(""),
    [lostPublicShare, setLostPublicShare] = useState(null),
    [sharePet, setSharePet] = useState(null),
    [shareMembers, setShareMembers] = useState([]),
    [shareTargetId, setShareTargetId] = useState(""),
    [sharePermission, setSharePermission] = useState("view"),
    [shareScope, setShareScope] = useState({ vet_contacts: false, medical_summary: false, insurance: false, documents: false }),
    [recordScanFile, setRecordScanFile] = useState(null),
    [recordScanConsent, setRecordScanConsent] = useState(false),
    [recordScanResult, setRecordScanResult] = useState(null),
    [recordScanBusy, setRecordScanBusy] = useState(false),
    [complete, setComplete] = useState(null),
    [recordDrawer, setRecordDrawer] = useState(null),
    [openSections, setOpenSections] = useState({ today: true, food: false, pulse: false, passports: false, insurance: false, connections: false, schedule: false, history: false }),
    [confirmAction, setConfirmAction] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [actionFeedback, setActionFeedback] = useState(null);
  const toggleSection = (key) => setOpenSections((value) => ({ ...value, [key]: !value[key] }));
  const load = useCallback(async () => {
    setError("");
    try {
      const next = await petsApi.snapshot();
      setData(next);
      setHealthPet((current) => current ? next.pets.find((row) => row.id === current.id) || null : null);
      setCare((v) => ({ ...v, pet_id: v.pet_id || next.pets[0]?.id || "" }));
      setRoutine((v) => ({ ...v, pet_id: v.pet_id || next.pets[0]?.id || "" }));
      setPetFood((v) => ({ ...v, pet_id: v.pet_id || next.pets[0]?.id || "" }));
      setClaim((v) => ({ ...v, pet_id: v.pet_id || next.pets[0]?.id || "" }));
    } catch (err) {
      setError(err.message || "Pet care could not be loaded.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setRecordScanFile(null);
    setRecordScanConsent(false);
    setRecordScanResult(null);
  }, [healthPet?.id]);
  useEffect(() => () => {
    if (lostPhotoUrl?.startsWith("blob:")) URL.revokeObjectURL(lostPhotoUrl);
  }, [lostPhotoUrl]);
  const reportAction = (message, tone = "success") => {
    setActionFeedback({ message, tone });
    window.setTimeout(() => setActionFeedback((current) => current?.message === message ? null : current), 4500);
  };
  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const field = document.createElement("textarea");
    field.value = text;
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand("copy");
    field.remove();
    if (!copied) throw new Error("Copy is unavailable in this browser.");
  };
  const shareOrCopy = async ({ title, text, files, copiedMessage }) => {
    try {
      const shareData = { title, text };
      if (files?.length && navigator.canShare?.({ files })) shareData.files = files;
      if (navigator.share) {
        await navigator.share(shareData);
        reportAction("Shared successfully.");
        return;
      }
      await copyText(text);
      reportAction(copiedMessage);
    } catch (err) {
      if (err?.name === "AbortError") {
        reportAction("Sharing was canceled.", "info");
        return;
      }
      try {
        await copyText(text);
        reportAction(`${copiedMessage} Your browser could not open its share menu.`, "info");
      } catch {
        reportAction("This browser could not share or copy the information. Try Print instead.", "error");
      }
    }
  };
  const run = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setError(err.message || "That change could not be saved.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const showSavingNotice = useMinLoad(busy && data !== null, 700);
  const addPet = async (e) => {
    e.preventDefault();
    if (
      await run(() =>
        editingPet ? petsApi.update(editingPet, pet) : petsApi.create(pet),
      )
    ) {
      setPet(EMPTY_PET);
      setEditingPet(null);
      setPetSheet(false);
      setNotice("Pet profile saved and connected care refreshed.");
    }
  };
  const openPet = (row) => {
    setEditingPet(row?.id || null);
    setPet(row ? { ...EMPTY_PET, ...row } : EMPTY_PET);
    setPetSheet(true);
  };
  const choosePetProfilePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const photo_url = await compressPetPhoto(file);
      if (photo_url.length > 500000) throw new Error("That image is still too large. Choose a smaller photo.");
      setPet((value) => ({ ...value, photo_url }));
    } catch (err) {
      reportAction(err.message || "The pet photo could not be prepared.", "error");
    }
  };
  const archivePet = async () => {
    if (!editingPet) return;
    if (await run(() => petsApi.remove(editingPet))) {
      setPetSheet(false);
      setEditingPet(null);
      setPet(EMPTY_PET);
      setConfirmAction(null);
      setNotice("Pet profile archived.");
    }
  };
  const restorePet = async (row) => {
    if (await run(() => petsApi.restore(row.id)))
      setNotice(`${row.name} and their care schedules were restored.`);
  };
  const performConfirmedAction = async () => {
    const action = confirmAction;
    if (!action) return;
    if (action.kind === "schedule") {
      if (await run(() => petsApi.removeSchedule(action.row.id)))
        setConfirmAction(null);
    } else if (action.kind === "routine") {
      if (await run(() => petsApi.removeRoutine(action.row.id))) {
        setConfirmAction(null);
        setNotice(`${action.row.title} removed from Pet Today.`);
      }
    } else if (action.kind === "claim") {
      if (await run(() => petsApi.removeInsuranceClaim(action.row.id))) {
        setConfirmAction(null);
        setNotice("Insurance claim removed.");
      }
    } else if (action.kind === "health") {
      if (await run(() => petsApi.removeHealthEntry(action.row.id))) {
        setConfirmAction(null);
        setNotice("Health observation removed.");
      }
    } else if (action.kind === "pet_food") {
      if (await run(() => petsApi.removeFood(action.row.id))) {
        setConfirmAction(null); setPetFoodSheet(false); setNotice("Pet food inventory removed.");
      }
    } else if (action.kind === "archive") {
      await archivePet();
    } else if (
      action.kind === "permanent" &&
      (await run(() => petsApi.removePermanent(action.row.id)))
    ) {
      setConfirmAction(null);
      setNotice(`${action.row.name} was permanently deleted.`);
    }
  };
  const addCare = async (e) => {
    e.preventDefault();
    const action = editingCare
      ? () => petsApi.updateSchedule(editingCare, care)
      : () => petsApi.createSchedule(care.pet_id, care);
    if (await run(action)) {
      setCare((v) => ({ ...EMPTY_CARE, pet_id: v.pet_id }));
      setEditingCare(null);
      setCareSheet(false);
      setNotice(editingCare ? "Care schedule updated and Home Care refreshed." : "Care schedule saved and Home Care refreshed.");
    }
  };
  const editCare = (row) => {
    setEditingCare(row.id);
    setCare({
      ...EMPTY_CARE,
      ...row,
      pet_id: row.pet_id,
      frequency_days: row.frequency_days ?? "",
      estimated_cost: row.estimated_cost ?? "",
      provider_id: row.provider_id || "",
      vault_item_id: row.vault_item_id || "",
    });
    setCareSheet(true);
  };
  const confirmCare = async (row) => {
    if (await run(() => petsApi.confirmSchedule(row.id))) {
      setNotice("Schedule confirmed and added to Home Care.");
    }
  };
  const finish = async (e) => {
    e.preventDefault();
    if (await run(() => petsApi.completeSchedule(complete.id, complete)))
      setComplete(null);
  };
  const completeFollowUp = async (row) => {
    if (await run(() => petsApi.updateFollowUp(row.id, { status: "completed" })))
      setNotice(`${row.pet_name}'s follow-up marked complete.`);
  };
  const addRoutine = async (e) => {
    e.preventDefault();
    const action = editingRoutine
      ? () => petsApi.updateRoutine(editingRoutine, { ...routine, ...routineTimes[0] })
      : async () => Promise.all(routineTimes.map((slot) => petsApi.createRoutine(routine.pet_id, { ...routine, ...slot, reminder_time: routine.reminder_enabled ? (slot.scheduled_time || routine.reminder_time) : "" })));
    if (await run(action)) {
      setRoutine((v) => ({ ...EMPTY_ROUTINE, pet_id: v.pet_id }));
      setRoutineTimes([{ time_of_day: "morning", scheduled_time: "" }]);
      setEditingRoutine(null);
      setRoutineSheet(false);
      setNotice(editingRoutine ? "Daily routine updated." : "Daily care routine added to Pet Today.");
    }
  };
  const editRoutine = (row) => {
    setEditingRoutine(row.id);
    setRoutineTimes([{ time_of_day: row.time_of_day || "anytime", scheduled_time: row.scheduled_time || "" }]);
    setRoutine({
      ...EMPTY_ROUTINE, ...row, pet_id: row.pet_id,
      remaining_quantity: row.remaining_quantity ?? "",
      dose_quantity: row.dose_quantity ?? "1",
      refill_threshold: row.refill_threshold ?? "5",
      supply_unit: row.supply_unit || "doses",
      reminder_enabled: !!row.reminder_enabled,
      reminder_time: row.reminder_time || "",
      pet_food_supply_id: row.pet_food_supply_id || "",
      consumption_quantity: row.consumption_quantity ?? "",
      low_stock_threshold: row.low_stock_threshold ?? "",
      duration_minutes: row.duration_minutes ?? "",
      distance: row.distance || "",
    });
    setRoutineSheet(true);
  };
  const savePetFood = async (e) => {
    e.preventDefault();
    const action = editingPetFood ? () => petsApi.updateFood(editingPetFood, petFood) : () => petsApi.createFood(petFood.pet_id, petFood);
    if (await run(action)) {
      setPetFood((v) => ({ ...EMPTY_PET_FOOD, pet_id: v.pet_id }));
      setEditingPetFood(null); setPetFoodSheet(false); setNotice("Pet food inventory updated.");
    }
  };
  const openPetFood = (row = null) => {
    setEditingPetFood(row?.id || null);
    setPetFood(row ? { ...EMPTY_PET_FOOD, ...row, pet_id: row.pet_id } : { ...EMPTY_PET_FOOD, pet_id: data.pets[0]?.id || "" });
    setPetFoodSheet(true);
  };
  const shopPetFood = async (row) => {
    if (await run(() => petsApi.addFoodToShopping(row.id))) setNotice(`${row.name} added to the Shopping card under Pet supplies.`);
  };
  const toggleRoutine = async (row) => {
    if (await run(() => petsApi.completeRoutine(row.id, !row.completed))) {
      setNotice(row.completed ? `${row.title} marked not done.` : `${row.pet_name}'s ${row.title} is done.`);
    }
  };
  const addHealthEntry = async (e) => {
    e.preventDefault();
    if (!healthPet) return;
    if (await run(() => petsApi.createHealthEntry(healthPet.id, healthEntry))) {
      setHealthEntry(EMPTY_HEALTH);
      setNotice(`${healthPet.name}'s health journal was updated.`);
    }
  };
  const openCaregiverBrief = async (row) => {
    setBusy(true);
    setError("");
    try { setCaregiverBrief(await petsApi.caregiverBrief(row.id)); }
    catch (err) { reportAction(err.message || "The caregiver brief could not be prepared.", "error"); }
    finally { setBusy(false); }
  };
  const openVetVisitBrief = async (row) => {
    setBusy(true); setError("");
    try { setVetVisitBrief(await petsApi.vetVisitBrief(row.id)); }
    catch (err) { reportAction(err.message || "The vet-visit brief could not be prepared.", "error"); }
    finally { setBusy(false); }
  };
  const shareVetVisitBrief = async () => {
    const brief = vetVisitBrief;
    if (!brief) return;
    const text = [`${brief.pet.name} · Vet visit preparation`, brief.pet.medical_conditions && `Conditions: ${brief.pet.medical_conditions}`, brief.pet.allergy_notes && `Allergies: ${brief.pet.allergy_notes}`, brief.pet.medication_notes && `Medications: ${brief.pet.medication_notes}`, ...brief.observations.map((row) => `${row.entry_date} · ${row.entry_type.replaceAll("_", " ")}${row.value_numeric != null ? `: ${row.value_numeric} ${row.unit || ""}` : row.notes ? `: ${row.notes}` : ""}`), "Questions:", ...brief.prompt_questions.map((question) => `• ${question}`), brief.disclaimer].filter(Boolean).join("\n");
    await shareOrCopy({ title: `${brief.pet.name} vet visit preparation`, text, copiedMessage: "Vet-visit brief copied." });
  };
  const saveClaim = async (e) => {
    e.preventDefault();
    const action = editingClaim ? () => petsApi.updateInsuranceClaim(editingClaim, claim) : () => petsApi.createInsuranceClaim(claim.pet_id, claim);
    if (await run(action)) { setClaim((v) => ({ ...EMPTY_CLAIM, pet_id: v.pet_id })); setEditingClaim(null); setClaimSheet(false); setNotice(editingClaim ? "Insurance claim updated." : "Insurance claim added."); }
  };
  const editClaim = (row) => { setEditingClaim(row.id); setClaim({ ...EMPTY_CLAIM, ...row, amount: row.amount ?? "", reimbursement_amount: row.reimbursement_amount ?? "", vault_item_id: row.vault_item_id || "" }); setClaimSheet(true); };
  const openLostCard = (row) => {
    setLostPet(row);
    setLostDetails(EMPTY_LOST);
    setLostPhotoFile(null);
    setLostPhotoUrl(row.photo_url || "");
    setLostPublicShare(null);
  };
  const closeLostCard = () => {
    setLostPet(null);
    setLostPhotoFile(null);
    setLostPhotoUrl("");
    setLostPublicShare(null);
  };
  const chooseLostPhoto = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      reportAction("Choose an image file for the lost-pet card.", "error");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      reportAction("Choose a pet photo smaller than 10 MB.", "error");
      return;
    }
    setLostPhotoFile(file);
    setLostPhotoUrl(URL.createObjectURL(file));
    reportAction("Photo added to this lost-pet card. It will not change the saved profile.", "info");
  };
  const shareLostCard = async () => {
    if (!lostPet) return;
    const text = [`LOST ${lostPet.species?.toUpperCase() || "PET"}: ${lostPet.name}`, lostPet.breed && `${lostPet.breed}${lostPet.color_markings ? ` · ${lostPet.color_markings}` : ""}`, lostPet.microchip_number && `Microchip: ${lostPet.microchip_number}`, lostDetails.last_seen_date && `Last seen: ${lostDetails.last_seen_date}${lostDetails.last_seen_location ? ` · ${lostDetails.last_seen_location}` : ""}`, lostDetails.notes, lostDetails.contact_name && `Contact: ${lostDetails.contact_name}`, lostDetails.contact_phone].filter(Boolean).join("\n");
    await shareOrCopy({ title: `Help find ${lostPet.name}`, text, files: lostPhotoFile ? [lostPhotoFile] : undefined, copiedMessage: lostPhotoFile ? "Lost-pet details copied. This browser could not attach the selected photo." : "Lost-pet card copied." });
  };
  const createLostPublicLink = async () => {
    if (!lostPet) return;
    setBusy(true);
    try {
      const result = await petsApi.createLostShare(lostPet.id, lostDetails);
      const share = { ...result, url: result.path ? `${window.location.origin}${result.path}` : result.url };
      setLostPublicShare(share);
      await copyText(share.url);
      reportAction("Secure lost-pet link created and copied. It expires in 7 days.");
    } catch (err) {
      reportAction(err.message || "The public lost-pet link could not be created.", "error");
    } finally { setBusy(false); }
  };
  const revokeLostPublicLink = async () => {
    if (!lostPublicShare) return;
    try { await petsApi.revokeLostShare(lostPublicShare.id); setLostPublicShare(null); reportAction("Public lost-pet link revoked.", "info"); }
    catch (err) { reportAction(err.message || "The public link could not be revoked.", "error"); }
  };
  const openPetShare = async (row) => {
    setSharePet(row); setShareMembers([]); setShareTargetId(""); setSharePermission("view");
    setShareScope({ vet_contacts: false, medical_summary: false, insurance: false, documents: false });
    setError(""); setBusy(true);
    try { const payload = await sharingApi.members(); const members = payload.members || []; setShareMembers(members); setShareTargetId(members[0]?.user_id ? String(members[0].user_id) : ""); if (!members.length) reportAction("Add another Family member in Settings before sharing.", "info"); }
    catch (err) { reportAction(err.message || "Family members could not be loaded.", "error"); }
    finally { setBusy(false); }
  };
  const savePetShare = async () => {
    if (!sharePet) return;
    if (!shareTargetId) {
      reportAction("Choose a Family member first. Add one in Settings if this list is empty.", "info");
      return;
    }
    setBusy(true); setError("");
    try { const result = await sharingApi.create({ resource_type: "pet", resource_id: sharePet.id, recipient_user_id: Number(shareTargetId), permission: sharePermission, scope: shareScope }); if (result.already_shared) { await sharingApi.updatePermission(result.share.id, sharePermission); await sharingApi.updateScope(result.share.id, shareScope); } const name = sharePet.name; setSharePet(null); reportAction(`${name} shared with your Family member.`); }
    catch (err) { reportAction(err.message || "The pet could not be shared.", "error"); }
    finally { setBusy(false); }
  };
  const shareCaregiverBrief = async () => {
    const p = caregiverBrief?.pet;
    if (!p) return;
    const lines = [
      `${p.name} · Caregiver brief`,
      p.feeding_notes && `Feeding: ${p.feeding_notes}`,
      p.allergy_notes && `Allergies: ${p.allergy_notes}`,
      p.medical_conditions && `Medical: ${p.medical_conditions}`,
      p.medication_notes && `Medication: ${p.medication_notes}`,
      p.primary_vet_name && `Vet: ${p.primary_vet_name}${p.primary_vet_phone ? ` · ${p.primary_vet_phone}` : ""}`,
      p.emergency_vet_name && `Emergency vet: ${p.emergency_vet_name}${p.emergency_vet_phone ? ` · ${p.emergency_vet_phone}` : ""}`,
      p.caregiver_notes && `Caregiver notes: ${p.caregiver_notes}`,
    ].filter(Boolean).join("\n");
    await shareOrCopy({ title: `${p.name} caregiver brief`, text: lines, copiedMessage: "Caregiver brief copied." });
  };
  const scanPetRecord = async () => {
    if (!healthPet || !recordScanFile || !recordScanConsent) return;
    setRecordScanBusy(true); setError(""); setRecordScanResult(null);
    try { setRecordScanResult(await petsApi.scanRecord(healthPet.id, recordScanFile, true)); }
    catch (err) { setError(err.message || "That pet record could not be read."); }
    finally { setRecordScanBusy(false); }
  };
  const reviewScannedCare = () => {
    const record = recordScanResult?.record;
    if (!record || !healthPet) return;
    setCare({ ...EMPTY_CARE, pet_id: healthPet.id, title: record.title || record.vaccine_name || record.medication_name || "", care_type: record.care_type || "other", next_due: record.next_due || "", frequency_days: record.frequency_days || "", estimated_cost: record.cost || "", dosage: record.dosage || "", instructions: record.instructions || record.notes || "" });
    setHealthPet(null); setCareSheet(true);
  };
  const reviewScannedProfile = () => {
    const record = recordScanResult?.record;
    if (!record || !healthPet) return;
    setEditingPet(healthPet.id);
    setPet({ ...EMPTY_PET, ...healthPet, medical_conditions: record.medical_conditions || healthPet.medical_conditions || "", allergy_notes: record.allergies || healthPet.allergy_notes || "", insurance_provider: record.insurance_provider || healthPet.insurance_provider || "", insurance_policy_number: record.insurance_policy_number || healthPet.insurance_policy_number || "" });
    setHealthPet(null); setPetSheet(true);
  };
  const saveScannedHistory = async () => {
    const record = recordScanResult?.record;
    if (!record || !healthPet) return;
    const payload = {
      reviewed: true,
      title: record.title || record.document_type?.replaceAll("_", " ") || "Veterinary record",
      care_date: record.care_date || record.visit_date || record.date || new Date().toLocaleDateString("en-CA"),
      care_type: record.care_type || (record.document_type === "vaccination" ? "vaccination" : "vet"),
      cost: record.cost || "",
      notes: record.instructions || record.notes || record.summary || "Imported from a reviewed pet record.",
    };
    if (await run(() => petsApi.importHistory(healthPet.id, payload))) {
      setRecordScanResult(null);
      setNotice("Reviewed historical record added to care history.");
    }
  };
  if (!data && error)
    return (
      <main className="hm-pets-page">
        <div className="hm-pets-error" role="alert">{error}</div>
        <button type="button" className="hm-pets-add-button" onClick={load}>Try again</button>
      </main>
    );
  if (!data)
    return (
      <main className="hm-pets-page">
        <div className="hm-pets-loading">Gathering pet care…</div>
      </main>
    );
  const petRow = (row) => (
    <button
      type="button"
      className="hm-pet-profile"
      key={row.id}
      onClick={() => openPet(row)}
    >
      <i className={`hm-pet-icon${row.photo_url ? " has-photo" : ""}`}>{row.photo_url ? <img src={row.photo_url} alt="" /> : petIcon(row.species)}</i>
      <span>
        <strong>{row.name}</strong>
        <small>
          {[
            row.breed,
            row.species,
            row.weight_lbs ? `${row.weight_lbs} lb` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </small>
        {row.allergy_notes && <em>Allergy: {row.allergy_notes}</em>}
      </span>
      <b>Edit ›</b>
    </button>
  );
  const careRow = (row) => (
    <article className="hm-pet-care-row" key={row.id}>
      <div className="hm-pet-care-main">
        <div className={`hm-pet-care-type is-${row.care_type}`}>
          {CARE_LABEL[row.care_type] || "Care"}
        </div>
        <div className="hm-pet-care-copy">
          <div className="hm-pet-care-heading">
            <strong>
              {row.pet_name}: {row.title}
            </strong>
            {row.is_suggested && (
              <em className="hm-pet-suggested-tag">Suggested</em>
            )}
          </div>
          <span>
            {row.next_due
              ? new Date(`${row.next_due}T12:00:00`).toLocaleDateString(
                  undefined,
                  { month: "short", day: "numeric", year: "numeric" },
                )
              : "No due date"}
            {row.provider_name ? ` · ${row.provider_name}` : ""}
          </span>
          {row.dosage && <small>{row.dosage}</small>}
        </div>
      </div>
      <div className="hm-pet-care-actions">
        {row.is_suggested ? (
          <button
            type="button"
            className="hm-pet-confirm-schedule"
            title="Confirm schedule"
            onClick={() => confirmCare(row)}
          >
            Confirm
          </button>
        ) : (
          <button
            onClick={() =>
              setComplete({
                id: row.id,
                care_date: new Date().toLocaleDateString("en-CA"),
                cost: "",
                notes: "",
                follow_up_date: "",
                follow_up_notes: "",
              })
            }
          >
            Complete
          </button>
        )}
        <button type="button" onClick={() => editCare(row)}>Edit</button>
        <button
          type="button"
          className="hm-pet-remove-icon"
          aria-label="Remove care schedule"
          title="Remove"
          onClick={() => setConfirmAction({ kind: "schedule", row })}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0 1 13a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-13" />
          </svg>
        </button>
      </div>
    </article>
  );
  const historyRow = (row) => (
    <article key={row.id}>
      <div>
        <strong>
          {row.pet_name}: {row.title}
        </strong>
        <span>
          {new Date(`${row.care_date}T12:00:00`).toLocaleDateString()}
        </span>
      </div>
      <b>{row.cost != null ? `$${row.cost.toFixed(2)}` : "No cost recorded"}</b>
      {row.follow_up_date && <p>Follow up {new Date(`${row.follow_up_date}T12:00:00`).toLocaleDateString()}{row.follow_up_notes ? ` · ${row.follow_up_notes}` : ""}</p>}
      {row.follow_up_status === "open" && <button type="button" onClick={() => completeFollowUp(row)}>Mark follow-up done</button>}
    </article>
  );
  const claimRow = (row) => (
    <article key={row.id}>
      <button type="button" onClick={() => editClaim(row)}>
        <span><strong>{row.pet_name}: {row.title}</strong><small>{new Date(`${row.claim_date}T12:00:00`).toLocaleDateString()} · {row.status.replaceAll("_", " ")}{row.claim_number ? ` · #${row.claim_number}` : ""}</small></span>
        <b>{row.amount != null ? `$${row.amount.toFixed(2)}` : "Amount pending"}</b>
      </button>
      <button type="button" aria-label={`Remove ${row.title}`} onClick={() => setConfirmAction({ kind: "claim", row })}>×</button>
    </article>
  );
  const previewLimit = 3;
  const petFoodRow = (row) => {
    const low = row.low_stock_threshold != null && Number(row.quantity) <= Number(row.low_stock_threshold);
    const expired = row.expiry_date && row.expiry_date <= new Date().toLocaleDateString("en-CA");
    return <article key={row.id} className={low || expired ? "needs-attention" : ""}><button type="button" className="hm-pet-food-main" onClick={() => openPetFood(row)}><i>🥣</i><span><strong>{row.pet_name}: {row.name}</strong><small>{row.quantity} {row.unit || "units"}{row.expiry_date ? ` · expires ${new Date(`${row.expiry_date}T12:00:00`).toLocaleDateString()}` : ""}</small></span><b>{expired ? "Check expiry" : low ? "Running low" : "In stock"}</b></button><button type="button" onClick={() => shopPetFood(row)}>Add to shopping</button></article>;
  };
  const sectionToggle = (key, label) => <button className="hm-pets-section-toggle" type="button" aria-label={`${openSections[key] ? "Collapse" : "Open"} ${label}`} aria-expanded={openSections[key]} onClick={() => toggleSection(key)}>{openSections[key] ? "⌄" : "›"}</button>;
  return (
    <main className="hm-pets-page">
      <datalist id="hm-pet-breed-suggestions">
        {(BREED_SUGGESTIONS[pet.species] || []).map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>
      <datalist id="hm-pet-allergy-suggestions">
        {ALLERGY_SUGGESTIONS.map((a) => (
          <option key={a} value={a} />
        ))}
      </datalist>
      <datalist id="hm-pet-feeding-suggestions">
        {FEEDING_ROUTINE_SUGGESTIONS.map((f) => (
          <option key={f} value={f} />
        ))}
      </datalist>
      <datalist id="hm-pet-care-title-suggestions">
        {(CARE_TITLE_SUGGESTIONS[care.care_type] || []).map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
      <section className="hm-pets-hero">
        <div>
          <span>Pets</span>
          <h1>Care that stays ahead</h1>
          <p>
            Profiles, routines, records, appointments, and costs connected to
            the HouseMind systems you already use.
          </p>
        </div>
        <div className="hm-pets-hero__count">
          <strong>{data.summary.pets}</strong>
          <small>{data.summary.pets === 1 ? "pet" : "pets"}</small>
        </div>
      </section>
      {error && <div className="hm-pets-error">{error}</div>}
      {notice && (
        <div className="hm-pets-notice" role="status">
          ✓ {notice}
        </div>
      )}
      <section className="hm-pets-summary">
        <div>
          <strong>{data.summary.due}</strong>
          <span>due now</span>
        </div>
        <div>
          <strong>{data.summary.upcoming}</strong>
          <span>upcoming</span>
        </div>
        <div>
          <strong>{data.summary.documents}</strong>
          <span>Vault records</span>
        </div>
      </section>
      <section className="hm-pets-card hm-pet-profiles">
        <header><div><span>Household pets</span><h2>{data.pets.length ? "Profiles and daily context" : "No pets added yet"}</h2></div><button className="hm-pets-add-button" type="button" onClick={() => openPet()}>+ Add pet</button></header>
        <div className="hm-pet-grid">{data.pets.slice(0, previewLimit).map(petRow)}</div>
        {data.pets.length > previewLimit && <button className="hm-pets-view-all" type="button" onClick={() => setRecordDrawer("pets")}>View all {data.pets.length} pets ›</button>}
        {!!data.archived_pets?.length && <button className="hm-pets-archived-link" type="button" onClick={() => setRecordDrawer("archived")}>Archived pets ({data.archived_pets.length}) ›</button>}
      </section>
      <section className="hm-pets-card hm-pet-today">
        <header>
          <div>
            <span>Pet Today</span>
            <h2>{data.today_routines.length ? `${data.summary.today_completed} of ${data.summary.today_routines} handled` : "Daily care, without the paperwork"}</h2>
          </div>
          <div className="hm-pets-header-actions"><button className="hm-pets-add-button" type="button" disabled={!data.pets.length} onClick={() => { setEditingRoutine(null); setRoutine((v) => ({ ...EMPTY_ROUTINE, pet_id: v.pet_id || data.pets[0]?.id || "" })); setRoutineTimes([{ time_of_day: "morning", scheduled_time: "" }]); setRoutineSheet(true); }}>+ Add routine</button>{sectionToggle("today", "Pet Today")}</div>
        </header>
        {openSections.today && (data.today_routines.length ? (
          <div className="hm-pet-today-list">
            {data.today_routines.slice(0, previewLimit).map((row) => (
              <article key={row.id} className={row.completed ? "is-complete" : ""}>
                <button type="button" className="hm-pet-routine-check" onClick={() => toggleRoutine(row)}>
                  <i>{row.completed ? "✓" : (ROUTINE_LABEL[row.routine_type] || "✓").split(" ")[0]}</i>
                  <span><strong>{row.pet_name}: {row.title}</strong><small>{row.scheduled_time || row.time_of_day}{row.duration_minutes ? ` · ${row.duration_minutes} min` : ""}{row.distance ? ` · ${row.distance}` : ""}{row.dosage ? ` · ${row.dosage}` : ""}{row.routine_type === "medication" && row.remaining_quantity != null ? ` · ${row.remaining_quantity} ${row.supply_unit || "doses"} left` : ""}{row.reminder_enabled ? ` · reminder ${row.reminder_time || row.time_of_day}` : ""}{row.pet_food_name && row.pet_food_remaining != null ? ` · ${row.pet_food_remaining} ${row.pet_food_name} left` : ""}</small>{row.pet_food_low && <em>Pet food is running low</em>}</span>
                  <b>{row.completed ? "Done" : "Mark done"}</b>
                </button>
                <button type="button" className="hm-pet-routine-remove" aria-label={`Remove ${row.title}`} onClick={() => setConfirmAction({ kind: "routine", row })}>×</button>
              </article>
            ))}
          </div>
        ) : <div className="hm-pets-empty">Add feeding, medication, walks, or other routines to make this the household’s daily pet-care check-in.</div>)}
        {openSections.today && data.today_routines.length > previewLimit && <button className="hm-pets-view-all" type="button" onClick={() => setRecordDrawer("today")}>View all {data.today_routines.length} routines ›</button>}
      </section>
      <section className="hm-pets-card hm-pet-food">
        <header><div><span>Pet food</span><h2>Stock and expiry</h2></div><div className="hm-pets-header-actions"><button className="hm-pets-add-button" type="button" disabled={!data.pets.length} onClick={() => openPetFood()}>+ Add food</button>{sectionToggle("food", "pet food")}</div></header>
        {openSections.food && ((data.pet_food_supplies || []).length ? <><div className="hm-pet-food-grid">{data.pet_food_supplies.slice(0, previewLimit).map(petFoodRow)}</div>{data.pet_food_supplies.length > previewLimit && <button className="hm-pets-view-all" type="button" onClick={() => setRecordDrawer("pet_food")}>View all {data.pet_food_supplies.length} food items ›</button>}</> : <div className="hm-pets-empty">Add pet food to track quantity and expiry.</div>)}
      </section>
      {!!data.pets.length && <section className="hm-pets-card hm-pet-care-pulse">
        <header><div><span>Care pulse</span><h2>Follow-through at a glance</h2></div>{sectionToggle("pulse", "care pulse")}</header>
        {openSections.pulse && <div className="hm-pet-pulse-grid">{data.pets.slice(0, previewLimit).map((row) => { const insight = data.care_insights?.[String(row.id)] || {}; return <article key={row.id}><div><i>{petIcon(row.species)}</i><span><strong>{row.name}</strong><small>{insight.expected_7d ? `${insight.completed_7d} of ${insight.expected_7d} routines completed in 7 days` : "Add daily routines to begin a care pulse"}</small></span><b>{insight.completion_percent_7d != null ? `${insight.completion_percent_7d}%` : "—"}</b></div><div className="hm-pet-pulse-meter"><i style={{ width: `${insight.completion_percent_7d || 0}%` }} /></div><p>{insight.next_care ? `Next: ${insight.next_care.title} · ${new Date(`${insight.next_care.next_due}T12:00:00`).toLocaleDateString()}` : "No upcoming scheduled care."}{insight.vet_followup_observations ? ` · ${insight.vet_followup_observations} observation${insight.vet_followup_observations === 1 ? "" : "s"} marked for vet follow-up.` : ""}</p><footer><span>${Number(insight.care_spend_12m || 0).toFixed(2)} care costs · ${Number(insight.claim_amount_open || 0).toFixed(2)} open claims</span><button type="button" onClick={() => openVetVisitBrief(row)}>Prepare for vet</button></footer></article>; })}</div>}
      </section>}
      <section className="hm-pets-card hm-pet-passports">
        <header><div><span>Health passports</span><h2>Care context ready when it matters</h2></div>{sectionToggle("passports", "health passports")}</header>
        {openSections.passports && <div className="hm-pet-passport-grid">
          {data.pets.slice(0, previewLimit).map((row) => {
            const recent = data.health_entries.filter((entry) => entry.pet_id === row.id)[0];
            const trend = data.health_trends?.[String(row.id)] || {};
            return <article key={row.id}>
              <div><i>{petIcon(row.species)}</i><span><strong>{row.name}</strong><small>{row.primary_vet_name || "Add veterinarian"}{row.weight_lbs ? ` · ${row.weight_lbs} lb` : ""}</small></span></div>
              <p>{recent ? `Latest: ${recent.entry_type.replaceAll("_", " ")} · ${new Date(`${recent.entry_date}T12:00:00`).toLocaleDateString()}${trend.weight_change != null ? ` · Weight ${trend.weight_change > 0 ? "+" : ""}${trend.weight_change} lb` : ""}` : "No health observations yet."}{trend.elevated_observations ? ` · ${trend.elevated_observations} concern${trend.elevated_observations === 1 ? "" : "s"} to discuss with a vet` : ""}</p>
              <footer><button type="button" onClick={() => setHealthPet(row)}>Open passport</button><button type="button" onClick={() => openCaregiverBrief(row)}>Caregiver brief</button><button type="button" onClick={() => openLostCard(row)}>Lost-pet card</button><button type="button" onClick={() => openPetShare(row)}>Share with family</button></footer>
            </article>;
          })}
        </div>}
      </section>
      <section className="hm-pets-card hm-pet-insurance">
        <header><div><span>Insurance claims</span><h2>Claims and reimbursements</h2></div><div className="hm-pets-header-actions"><button className="hm-pets-add-button" type="button" disabled={!data.pets.length} onClick={() => { setEditingClaim(null); setClaim((v) => ({ ...EMPTY_CLAIM, pet_id: v.pet_id || data.pets[0]?.id || "" })); setClaimSheet(true); }}>+ Add claim</button>{sectionToggle("insurance", "insurance claims")}</div></header>
        {openSections.insurance && <>{data.insurance_claims?.length ? <div className="hm-pet-claim-list">{data.insurance_claims.slice(0, previewLimit).map(claimRow)}</div> : <div className="hm-pets-empty">Add a claim to track its status and reimbursement.</div>}{data.insurance_claims?.length > previewLimit && <button className="hm-pets-view-all" type="button" onClick={() => setRecordDrawer("claims")}>View all {data.insurance_claims.length} claims ›</button>}{!!data.pets.length && <div className="hm-pet-expense-summary">{data.pets.slice(0, previewLimit).map((row) => { const analytics = data.expense_analytics?.[String(row.id)] || {}; const year = String(new Date().getFullYear()); const categories = Object.entries(analytics.by_category || {}).sort((a, b) => b[1] - a[1]); return <article key={row.id}><span><strong>{row.name}</strong><small>{year} recorded care</small></span><b>${Number(analytics.by_year?.[year] || 0).toFixed(2)}</b>{categories.length > 0 && <p>{categories.slice(0, 3).map(([category, amount]) => `${category} $${Number(amount).toFixed(0)}`).join(" · ")}</p>}</article>; })}</div>}</>}
      </section>
      <section className="hm-pets-card">
        <header>
          <div>
            <span>Care schedule</span>
            <h2>Medication, visits, and routines</h2>
          </div>
          <div className="hm-pets-header-actions"><button className="hm-pets-add-button" type="button" disabled={!data.pets.length} onClick={() => {
              setEditingCare(null);
              setCare((value) => ({ ...EMPTY_CARE, pet_id: value.pet_id || data.pets[0]?.id || "" }));
              setCareSheet(true);
            }}>+ Add care</button>{sectionToggle("schedule", "care schedule")}</div>
        </header>
        {openSections.schedule && <><div className="hm-pet-care-list">
          {data.schedules.slice(0, previewLimit).map(careRow)}
        </div>
        {data.schedules.length > previewLimit && (
          <button
            className="hm-pets-view-all"
            type="button"
            onClick={() => setRecordDrawer("care")}
          >
            View all {data.schedules.length} care items ›
          </button>
        )}
        {!data.pets.length && (
          <div className="hm-pets-empty">
            Add a pet before creating care schedules.
          </div>
        )}
        </>}
      </section>
      <section
        className={`hm-pets-card hm-pets-history-card${!data.history.length ? " is-empty" : ""}`}
      >
        <header>
          <div>
            <span>Care history</span>
            <h2>Completed records and spending</h2>
          </div>
          {sectionToggle("history", "care history")}
        </header>
        {openSections.history && <><div className="hm-pet-history">
          {data.history.slice(0, previewLimit).map(historyRow)}
          {!data.history.length && (
            <div className="hm-pets-empty">Completed care and recorded costs appear here.</div>
          )}
        </div>
        {data.history.length > previewLimit && (
          <button
            className="hm-pets-view-all"
            type="button"
            onClick={() => setRecordDrawer("history")}
          >
            View all {data.history.length} completed records ›
          </button>
        )}
        </>}
      </section>
      <ModuleAskBar
        module="pets"
        label="Pets"
        placeholder="Ask about pet care…"
      />
      {recordDrawer && (
        <div
          className="hm-pets-sheet-bg"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setRecordDrawer(null);
          }}
        >
          <section
            className="hm-pets-record-drawer"
            role="dialog"
            aria-modal="true"
          >
            <SheetHandle
              onClose={() => setRecordDrawer(null)}
              label="Close pet records"
            />
            <header>
              <div>
                <span>Pet care records</span>
                <h2>
                  {recordDrawer === "pets"
                    ? `All pets (${data.pets.length})`
                    : recordDrawer === "today"
                      ? `Pet Today (${data.today_routines.length})`
                    : recordDrawer === "care"
                      ? `Care schedule (${data.schedules.length})`
                      : recordDrawer === "archived"
                      ? `Archived pets (${data.archived_pets.length})`
                      : recordDrawer === "claims"
                        ? `Insurance claims (${data.insurance_claims.length})`
                        : recordDrawer === "pet_food"
                          ? `Pet food (${data.pet_food_supplies.length})`
                        : `Care history (${data.history.length})`}
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setRecordDrawer(null)}
              >
                ×
              </button>
            </header>
            <div
              className={
                recordDrawer === "pets"
                  ? "hm-pet-grid"
                  : recordDrawer === "today"
                    ? "hm-pet-today-list"
                  : recordDrawer === "care"
                    ? "hm-pet-care-list"
                    : recordDrawer === "claims"
                      ? "hm-pet-claim-list"
                    : recordDrawer === "pet_food"
                      ? "hm-pet-food-grid"
                    : recordDrawer === "archived"
                      ? "hm-pet-archived-list"
                      : "hm-pet-history"
              }
            >
              {recordDrawer === "pets" && data.pets.map(petRow)}
              {recordDrawer === "today" && data.today_routines.map((row) => (
                <article key={row.id} className={row.completed ? "is-complete" : ""}><button type="button" className="hm-pet-routine-check" onClick={() => toggleRoutine(row)}><i>{row.completed ? "✓" : (ROUTINE_LABEL[row.routine_type] || "✓").split(" ")[0]}</i><span><strong>{row.pet_name}: {row.title}</strong><small>{row.time_of_day}{row.dosage ? ` · ${row.dosage}` : ""}</small></span><b>{row.completed ? "Done" : "Mark done"}</b></button><button type="button" className="hm-pet-routine-remove" aria-label={`Remove ${row.title}`} onClick={() => setConfirmAction({ kind: "routine", row })}>×</button></article>
              ))}
              {recordDrawer === "care" && data.schedules.map(careRow)}
              {recordDrawer === "history" && data.history.map(historyRow)}
              {recordDrawer === "claims" && data.insurance_claims.map(claimRow)}
              {recordDrawer === "pet_food" && data.pet_food_supplies.map(petFoodRow)}
              {recordDrawer === "archived" &&
                data.archived_pets.map((row) => (
                  <article key={row.id}>
                    <i className="hm-pet-icon">{petIcon(row.species)}</i>
                    <span>
                      <strong>{row.name}</strong>
                      <small>
                        {[row.breed, row.species].filter(Boolean).join(" · ")}
                      </small>
                    </span>
                    <div>
                      <button type="button" onClick={() => restorePet(row)}>
                        Restore
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() =>
                          setConfirmAction({ kind: "permanent", row })
                        }
                      >
                        Delete permanently
                      </button>
                    </div>
                  </article>
                ))}
            </div>
          </section>
        </div>
      )}
      {petSheet && (
        <div
          className="hm-pets-sheet-bg"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPetSheet(false);
          }}
        >
          <form className="hm-pets-sheet hm-pets-form" onSubmit={addPet}>
            <SheetHandle
              onClose={() => setPetSheet(false)}
              label="Close pet form"
            />
            <header className="wide">
              <div>
                <span>{editingPet ? "Edit pet" : "New pet"}</span>
                <h2>{editingPet ? pet.name : "Add a pet"}</h2>
              </div>
              <button type="button" onClick={() => setPetSheet(false)}>
                ×
              </button>
            </header>
            <section className="wide hm-pet-profile-photo">
              <div>{pet.photo_url ? <img src={pet.photo_url} alt={`${pet.name || "Pet"} profile`} /> : <i>{petIcon(pet.species)}</i>}</div>
              <span><strong>Profile photo</strong><small>Used on the pet profile and public Lost Pet page.</small><label><input type="file" accept="image/*" onChange={choosePetProfilePhoto} />Upload photo</label>{pet.photo_url && <button type="button" className="quiet" onClick={() => setPet((value) => ({ ...value, photo_url: "" }))}>Remove photo</button>}</span>
            </section>
            <label>
              Name
              <input
                required
                value={pet.name}
                onChange={(e) =>
                  setPet((v) => ({ ...v, name: e.target.value }))
                }
              />
            </label>
            <label>
              Species
              <select
                value={pet.species}
                onChange={(e) =>
                  setPet((v) => ({ ...v, species: e.target.value, breed: "" }))
                }
              >
                {[
                  "dog",
                  "cat",
                  "bird",
                  "fish",
                  "rabbit",
                  "reptile",
                  "other",
                ].map((x) => (
                  <option key={x} value={x}>
                    {x[0].toUpperCase() + x.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Breed
              <input
                list="hm-pet-breed-suggestions"
                value={pet.breed || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, breed: e.target.value }))
                }
              />
            </label>
            <label>
              Sex
              <select
                value={pet.sex || ""}
                onChange={(e) => setPet((v) => ({ ...v, sex: e.target.value }))}
              >
                <option value="">Not specified</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="unknown">Unknown</option>
              </select>
            </label>
            <label>
              Birthday
              <input
                type="date"
                value={pet.birth_date || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, birth_date: e.target.value }))
                }
              />
            </label>
            <label>
              Adoption date
              <input
                type="date"
                value={pet.adoption_date || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, adoption_date: e.target.value }))
                }
              />
            </label>
            <label>
              Weight (lb)
              <input
                type="number"
                min="0"
                step="0.1"
                value={pet.weight_lbs || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, weight_lbs: e.target.value }))
                }
              />
            </label>
            <label>
              Microchip
              <input
                value={pet.microchip_number || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, microchip_number: e.target.value }))
                }
              />
            </label>
            <label className="wide">
              Feeding routine
              <input
                list="hm-pet-feeding-suggestions"
                value={pet.feeding_notes || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, feeding_notes: e.target.value }))
                }
              />
            </label>
            <label>
              Allergies
              <input
                list="hm-pet-allergy-suggestions"
                value={pet.allergy_notes || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, allergy_notes: e.target.value }))
                }
              />
            </label>
            <label>
              Caregiver notes
              <input
                value={pet.caregiver_notes || ""}
                onChange={(e) =>
                  setPet((v) => ({ ...v, caregiver_notes: e.target.value }))
                }
              />
            </label>
            <label className="wide hm-pet-form-section">Health passport</label>
            <label>
              Medical conditions
              <input value={pet.medical_conditions || ""} onChange={(e) => setPet((v) => ({ ...v, medical_conditions: e.target.value }))} />
            </label>
            <label>
              Current medications
              <input value={pet.medication_notes || ""} onChange={(e) => setPet((v) => ({ ...v, medication_notes: e.target.value }))} />
            </label>
            <label>
              Primary veterinarian
              <input value={pet.primary_vet_name || ""} onChange={(e) => setPet((v) => ({ ...v, primary_vet_name: e.target.value }))} />
            </label>
            <label>
              Vet phone
              <input type="tel" value={pet.primary_vet_phone || ""} onChange={(e) => setPet((v) => ({ ...v, primary_vet_phone: e.target.value }))} />
            </label>
            <label>
              Emergency veterinarian
              <input value={pet.emergency_vet_name || ""} onChange={(e) => setPet((v) => ({ ...v, emergency_vet_name: e.target.value }))} />
            </label>
            <label>
              Emergency-vet phone
              <input type="tel" value={pet.emergency_vet_phone || ""} onChange={(e) => setPet((v) => ({ ...v, emergency_vet_phone: e.target.value }))} />
            </label>
            <label>
              Insurance provider
              <input value={pet.insurance_provider || ""} onChange={(e) => setPet((v) => ({ ...v, insurance_provider: e.target.value }))} />
            </label>
            <label>
              Policy number
              <input value={pet.insurance_policy_number || ""} onChange={(e) => setPet((v) => ({ ...v, insurance_policy_number: e.target.value }))} />
            </label>
            <footer className="wide">
              {editingPet && (
                <button
                  type="button"
                  className="danger"
                  onClick={() =>
                    setConfirmAction({
                      kind: "archive",
                      row: { id: editingPet, name: pet.name },
                    })
                  }
                >
                  Archive pet
                </button>
              )}
              <button
                type="button"
                className="quiet"
                onClick={() => setPetSheet(false)}
              >
                Cancel
              </button>
              <button disabled={busy}>{busy ? "Saving…" : "Save pet"}</button>
            </footer>
          </form>
        </div>
      )}
      {petFoodSheet && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setPetFoodSheet(false); }}>
          <form className="hm-pets-sheet hm-pets-form" onSubmit={savePetFood} role="dialog" aria-modal="true" aria-label={editingPetFood ? "Update pet food" : "Add pet food"}>
            <SheetHandle onClose={() => setPetFoodSheet(false)} label="Close pet food form" />
            <header className="wide"><div><span>Pet food inventory</span><h2>{editingPetFood ? "Update food stock" : "Add food stock"}</h2></div><button type="button" onClick={() => setPetFoodSheet(false)}>×</button></header>
            <label>Pet<select required disabled={!!editingPetFood} value={petFood.pet_id} onChange={(e) => setPetFood((v) => ({ ...v, pet_id: e.target.value }))}>{data.pets.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</select></label>
            <label>Food type<select required value={!petFood.name ? "" : PET_FOOD_SUGGESTIONS.includes(petFood.name) ? petFood.name : "custom"} onChange={(e) => setPetFood((v) => ({ ...v, name: e.target.value === "custom" ? "Other pet food" : e.target.value }))}><option value="" disabled>Select a common pet food</option>{PET_FOOD_SUGGESTIONS.map((value) => <option key={value} value={value}>{value}</option>)}<option value="custom">Other / custom food</option></select></label>
            {!!petFood.name && !PET_FOOD_SUGGESTIONS.includes(petFood.name) && <label>Custom food name<input required value={petFood.name === "Other pet food" ? "" : petFood.name} placeholder="Enter the food name" onChange={(e) => setPetFood((v) => ({ ...v, name: e.target.value }))} /></label>}
            <label>Quantity remaining<input required type="number" min="0" step="0.1" value={petFood.quantity} onChange={(e) => setPetFood((v) => ({ ...v, quantity: e.target.value }))} /></label>
            <label>Unit<input value={petFood.unit} placeholder="cups, cans, lb" onChange={(e) => setPetFood((v) => ({ ...v, unit: e.target.value }))} /></label>
            <label>Expiry date<input type="date" value={petFood.expiry_date || ""} onChange={(e) => setPetFood((v) => ({ ...v, expiry_date: e.target.value }))} /></label>
            <label>Warn when at<input type="number" min="0" step="0.1" value={petFood.low_stock_threshold ?? ""} onChange={(e) => setPetFood((v) => ({ ...v, low_stock_threshold: e.target.value }))} /></label>
            <p className="wide">This stock stays in Pets. Use “Add to shopping” to send a refill to the shared Shopping card.</p>
            <footer className="wide">{editingPetFood && <button type="button" className="danger" onClick={() => setConfirmAction({ kind: "pet_food", row: petFood })}>Remove</button>}<button type="button" className="quiet" onClick={() => setPetFoodSheet(false)}>Cancel</button><button disabled={busy}>{busy ? "Saving…" : "Save pet food"}</button></footer>
          </form>
        </div>
      )}
      {routineSheet && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setRoutineSheet(false); }}>
          <form className="hm-pets-sheet hm-pets-form" onSubmit={addRoutine}>
            <SheetHandle onClose={() => setRoutineSheet(false)} label="Close routine form" />
            <header className="wide"><div><span>Pet Today</span><h2>{editingRoutine ? "Update daily routine" : "Add a daily routine"}</h2></div><button type="button" onClick={() => setRoutineSheet(false)}>×</button></header>
            <label>Pet<select required disabled={!!editingRoutine} value={routine.pet_id} onChange={(e) => setRoutine((v) => ({ ...v, pet_id: e.target.value }))}>{data.pets.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</select></label>
            <label>Routine<input required value={routine.title} placeholder="Morning medication" onChange={(e) => setRoutine((v) => ({ ...v, title: e.target.value }))} /></label>
            <label>Type<select value={routine.routine_type} onChange={(e) => setRoutine((v) => ({ ...v, routine_type: e.target.value, dosage: "", duration_minutes: "", distance: "", pet_food_supply_id: "", consumption_quantity: "" }))}>{Object.entries(ROUTINE_LABEL).map(([value, label]) => <option key={value} value={value}>{label.replace(/^\S+\s/, "")}</option>)}</select></label><label className="hm-pet-remind-toggle">Remind me<span className="hm-pet-remind-toggle-row"><input type="checkbox" checked={routine.reminder_enabled} onChange={(e) => setRoutine((v) => ({ ...v, reminder_enabled: e.target.checked }))} /><b>{routine.reminder_enabled ? "On" : "Off"}</b></span></label>
            <section className="wide hm-pet-routine-times"><header><div><strong>🕐 Times</strong><small>Each becomes its own check-off</small></div>{!editingRoutine && <button type="button" className="hm-pet-add-time" onClick={() => setRoutineTimes((rows) => [...rows, { time_of_day: "afternoon", scheduled_time: "" }])}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>Add time</button>}</header>{routineTimes.map((slot, index) => <div key={index} className="hm-pet-time-slot"><select aria-label={`Time ${index + 1} of day`} value={slot.time_of_day} onChange={(e) => setRoutineTimes((rows) => rows.map((row, i) => i === index ? { ...row, time_of_day: e.target.value } : row))}>{["morning", "afternoon", "evening", "bedtime", "anytime"].map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select><span className="hm-pet-time-field"><input type="time" aria-label={`Time ${index + 1} exact time`} value={slot.scheduled_time} onChange={(e) => setRoutineTimes((rows) => rows.map((row, i) => i === index ? { ...row, scheduled_time: e.target.value } : row))} /></span>{routineTimes.length > 1 ? <button type="button" className="hm-pet-time-slot-remove" aria-label={`Remove time ${index + 1}`} onClick={() => setRoutineTimes((rows) => rows.filter((_, i) => i !== index))}>×</button> : <span />}</div>)}</section>
            <fieldset className="wide hm-pet-day-picker"><legend>Repeat on</legend>{DAY_LABELS.map((label, index) => <label key={`${label}-${index}`}><input type="checkbox" checked={routine.days_of_week.includes(index)} onChange={() => setRoutine((v) => ({ ...v, days_of_week: v.days_of_week.includes(index) ? v.days_of_week.filter((day) => day !== index) : [...v.days_of_week, index].sort() }))} /><span>{label}</span></label>)}</fieldset>
            <p className="wide hm-pet-form-note">Completing a routine checks off only that date. It will appear fresh on the next selected weekday—for example, completing Monday does not complete Wednesday, Friday, or Sunday.</p>
            {["feeding", "medication"].includes(routine.routine_type) && <label>{routine.routine_type === "medication" ? "Dosage" : "Portion"}<input value={routine.dosage} placeholder={routine.routine_type === "medication" ? "1 tablet" : "1 cup"} onChange={(e) => setRoutine((v) => ({ ...v, dosage: e.target.value }))} /></label>}
            {["walk", "exercise"].includes(routine.routine_type) && <><label>Duration <small>(minutes)</small><input type="number" min="1" value={routine.duration_minutes} onChange={(e) => setRoutine((v) => ({ ...v, duration_minutes: e.target.value }))} /></label><label>Distance <small>(optional)</small><input value={routine.distance} placeholder="1 mile" onChange={(e) => setRoutine((v) => ({ ...v, distance: e.target.value }))} /></label></>}
            <label className={["feeding", "medication"].includes(routine.routine_type) ? undefined : "wide"}>Instructions<input value={routine.instructions} onChange={(e) => setRoutine((v) => ({ ...v, instructions: e.target.value }))} /></label>
            {routine.reminder_enabled && routineTimes.some((slot) => !slot.scheduled_time) && <label className="wide">Reminder time<input type="time" value={routine.reminder_time} onChange={(e) => setRoutine((v) => ({ ...v, reminder_time: e.target.value }))} /></label>}
            {routine.routine_type === "feeding" && <section className="wide hm-pet-medication-supply"><h3>Pet food stock</h3><label>Pet food<select value={routine.pet_food_supply_id} onChange={(e) => setRoutine((v) => ({ ...v, pet_food_supply_id: e.target.value }))}><option value="">Not connected</option>{(data.pet_food_supplies || []).filter((item) => String(item.pet_id) === String(routine.pet_id)).map((item) => <option key={item.id} value={item.id}>{item.name} · {item.quantity} {item.unit || ""}</option>)}</select></label><label>Used per completion<input type="number" min="0" step="0.1" value={routine.consumption_quantity} onChange={(e) => setRoutine((v) => ({ ...v, consumption_quantity: e.target.value }))} /></label></section>}
            {routine.routine_type === "medication" && <section className="wide hm-pet-medication-supply"><h3>Medication supply</h3><label>Quantity remaining<input type="number" min="0" step="0.1" value={routine.remaining_quantity} onChange={(e) => setRoutine((v) => ({ ...v, remaining_quantity: e.target.value }))} /></label><label>Used per completion<input type="number" min="0" step="0.1" value={routine.dose_quantity} onChange={(e) => setRoutine((v) => ({ ...v, dose_quantity: e.target.value }))} /></label><label>Unit<input value={routine.supply_unit} placeholder="tablets" onChange={(e) => setRoutine((v) => ({ ...v, supply_unit: e.target.value }))} /></label><label>Warn when at<input type="number" min="0" step="0.1" value={routine.refill_threshold} onChange={(e) => setRoutine((v) => ({ ...v, refill_threshold: e.target.value }))} /></label></section>}
            {!!data.routines.length && <section className="wide hm-pet-routine-manager"><h3>Saved routines</h3>{data.routines.map((row) => <article key={row.id}><span><strong>{row.pet_name}: {row.title}</strong><small>{row.time_of_day} · {(row.days_of_week || []).length === 7 ? "daily" : `${row.days_of_week.length} days/week`}{row.remaining_quantity != null ? ` · ${row.remaining_quantity} ${row.supply_unit || "doses"} left` : ""}</small></span><div><button type="button" onClick={() => editRoutine(row)}>Edit</button><button type="button" aria-label={`Remove ${row.title}`} onClick={() => setConfirmAction({ kind: "routine", row })}>Remove</button></div></article>)}</section>}
            <footer className="wide hm-pet-routine-footer"><button type="button" className="quiet" onClick={() => setRoutineSheet(false)}>Cancel</button><button disabled={busy}>{busy ? "Saving…" : editingRoutine ? "Update routine" : "Add routine"}</button></footer>
          </form>
        </div>
      )}
      {healthPet && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setHealthPet(null); }}>
          <section className="hm-pets-sheet hm-pet-passport-sheet" role="dialog" aria-modal="true" aria-label={`${healthPet.name} health passport`}>
            <SheetHandle onClose={() => setHealthPet(null)} label="Close health passport" />
            <header><div><span>Health passport</span><h2>{petIcon(healthPet.species)} {healthPet.name}</h2></div><button type="button" onClick={() => setHealthPet(null)}>×</button></header>
            <div className="hm-pet-passport-facts">
              <div><small>Weight</small><strong>{healthPet.weight_lbs ? `${healthPet.weight_lbs} lb` : "Not recorded"}</strong></div>
              <div><small>Microchip</small><strong>{healthPet.microchip_number || "Not recorded"}</strong>{healthPet.microchip_number && <a href="https://www.aaha.org/for-veterinary-professionals/microchip-search/" target="_blank" rel="noreferrer">Open registry lookup ↗</a>}</div>
              <div><small>Primary vet</small><strong>{healthPet.primary_vet_name || "Not recorded"}</strong>{healthPet.primary_vet_phone && <a href={`tel:${healthPet.primary_vet_phone}`}>{healthPet.primary_vet_phone}</a>}</div>
              <div><small>Emergency vet</small><strong>{healthPet.emergency_vet_name || "Not recorded"}</strong>{healthPet.emergency_vet_phone && <a href={`tel:${healthPet.emergency_vet_phone}`}>{healthPet.emergency_vet_phone}</a>}</div>
              <div><small>Allergies</small><strong>{healthPet.allergy_notes || "None recorded"}</strong></div>
              <div><small>Medical conditions</small><strong>{healthPet.medical_conditions || "None recorded"}</strong></div>
              <div><small>Medications</small><strong>{healthPet.medication_notes || "None recorded"}</strong></div>
              <div><small>Insurance</small><strong>{healthPet.insurance_provider || "Not recorded"}</strong><span>{healthPet.insurance_policy_number}</span></div>
            </div>
            <button type="button" className="hm-pet-edit-passport" onClick={() => { setHealthPet(null); openPet(healthPet); }}>Edit passport details</button>
            <section className="hm-pet-trends"><div><span>Health trends</span><h3>Factual history, not a diagnosis</h3></div><WeightTrend points={data.health_trends?.[String(healthPet.id)]?.weight_history} />{Object.keys(data.health_trends?.[String(healthPet.id)]?.observation_counts_30d || {}).length > 0 && <div className="hm-pet-observation-counts">{Object.entries(data.health_trends[String(healthPet.id)].observation_counts_30d).map(([type, count]) => <span key={type}><b>{count}</b> {type.replaceAll("_", " ")}</span>)}</div>}<small>Observations in the last 30 days can help you describe changes to your veterinarian.</small></section>
            <section className="hm-pet-record-scan">
              <div><span>Scan a pet record</span><h3>Turn paperwork into a reviewable draft</h3><p>Vaccination certificate, visit summary, prescription, insurance, or adoption record.</p></div>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" onChange={(e) => { setRecordScanFile(e.target.files?.[0] || null); setRecordScanResult(null); }} />
              <label><input type="checkbox" checked={recordScanConsent} onChange={(e) => setRecordScanConsent(e.target.checked)} /> I consent to AI reading this document. I will review every field before saving.</label>
              <button type="button" disabled={!recordScanFile || !recordScanConsent || recordScanBusy} onClick={scanPetRecord}>{recordScanBusy ? "Reading record…" : "Scan for review"}</button>
              {recordScanResult?.record && <div className="hm-pet-scan-result"><span>{recordScanResult.confidence} confidence · editable draft</span>{recordScanResult.warnings?.map((warning) => <p key={warning}>⚠ {warning}</p>)}<label>Record title<input value={recordScanResult.record.title || ""} onChange={(e) => setRecordScanResult((value) => ({ ...value, record: { ...value.record, title: e.target.value } }))} /></label><label>Record date<input type="date" value={recordScanResult.record.care_date || recordScanResult.record.visit_date || recordScanResult.record.date || ""} onChange={(e) => setRecordScanResult((value) => ({ ...value, record: { ...value.record, care_date: e.target.value } }))} /></label><label>Record type<select value={recordScanResult.record.care_type || "other"} onChange={(e) => setRecordScanResult((value) => ({ ...value, record: { ...value.record, care_type: e.target.value } }))}>{Object.entries(CARE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><label>Notes<textarea rows="2" value={recordScanResult.record.notes || recordScanResult.record.summary || ""} onChange={(e) => setRecordScanResult((value) => ({ ...value, record: { ...value.record, notes: e.target.value } }))} /></label><small>No medical information has been saved. Check every field before choosing a destination.</small><footer><button type="button" onClick={reviewScannedProfile}>Review profile fields</button><button type="button" onClick={reviewScannedCare}>Review care schedule</button><button type="button" onClick={saveScannedHistory}>Save reviewed history</button></footer></div>}
            </section>
            <form className="hm-pet-health-entry" onSubmit={addHealthEntry}>
              <h3>Log a health observation</h3>
              <label>Type<select value={healthEntry.entry_type} onChange={(e) => setHealthEntry((v) => ({ ...v, entry_type: e.target.value, unit: e.target.value === "weight" ? "lb" : "" }))}>{["weight", "symptom", "appetite", "water", "energy", "bathroom", "behavior", "medication_reaction", "note"].map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}</select></label>
              <label>Date<input type="date" value={healthEntry.entry_date} onChange={(e) => setHealthEntry((v) => ({ ...v, entry_date: e.target.value }))} /></label>
              {healthEntry.entry_type === "weight" && <label>Weight<input required type="number" min="0" step="0.1" value={healthEntry.value_numeric} onChange={(e) => setHealthEntry((v) => ({ ...v, value_numeric: e.target.value }))} /></label>}
              {healthEntry.entry_type !== "weight" && <label>Concern<select value={healthEntry.severity} onChange={(e) => setHealthEntry((v) => ({ ...v, severity: e.target.value }))}><option value="">Observation only</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="urgent">Urgent—contact vet</option></select></label>}
              <label className="wide">Notes<textarea rows="3" value={healthEntry.notes} placeholder="What changed? Include duration and context." onChange={(e) => setHealthEntry((v) => ({ ...v, notes: e.target.value }))} /></label>
              <button className="wide" disabled={busy}>{busy ? "Saving…" : "Save observation"}</button>
              <small className="wide">HouseMind records your observations; it does not diagnose medical conditions. Contact a veterinarian for health concerns.</small>
            </form>
            <div className="hm-pet-health-history"><header><h3>Health journal</h3><select aria-label="Health history type" value={healthType} onChange={(e) => setHealthType(e.target.value)}><option value="all">All types</option>{["weight", "symptom", "appetite", "water", "energy", "bathroom", "behavior", "medication_reaction", "note"].map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}</select><select aria-label="Health history range" value={healthRange} onChange={(e) => setHealthRange(e.target.value)}><option value="30">30 days</option><option value="90">90 days</option><option value="365">1 year</option><option value="all">All</option></select></header>{data.health_entries.filter((entry) => entry.pet_id === healthPet.id && (healthType === "all" || entry.entry_type === healthType) && (healthRange === "all" || new Date(`${entry.entry_date}T12:00:00`) >= new Date(Date.now() - Number(healthRange) * 86400000))).map((entry) => <article key={entry.id}><div><strong>{entry.entry_type.replaceAll("_", " ")}</strong><span>{new Date(`${entry.entry_date}T12:00:00`).toLocaleDateString()}</span></div><b>{entry.value_numeric != null ? `${entry.value_numeric} ${entry.unit || ""}` : entry.severity || "Recorded"}</b>{entry.notes && <p>{entry.notes}</p>}<button type="button" aria-label={`Remove ${entry.entry_type.replaceAll("_", " ")} observation`} onClick={() => setConfirmAction({ kind: "health", row: { ...entry, pet_name: healthPet.name } })}>Remove</button></article>)}</div>
          </section>
        </div>
      )}
      {claimSheet && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setClaimSheet(false); }}>
          <form className="hm-pets-sheet hm-pets-form" onSubmit={saveClaim}>
            <SheetHandle onClose={() => setClaimSheet(false)} label="Close insurance claim" />
            <header className="wide"><div><span>Pet insurance</span><h2>{editingClaim ? "Update claim" : "Add claim"}</h2></div><button type="button" onClick={() => setClaimSheet(false)}>×</button></header>
            <label>Pet<select required value={claim.pet_id} onChange={(e) => setClaim((v) => ({ ...v, pet_id: e.target.value }))}>{data.pets.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</select></label>
            <label>Claim title<input required value={claim.title} placeholder="Emergency visit" onChange={(e) => setClaim((v) => ({ ...v, title: e.target.value }))} /></label>
            <label>Service/claim date<input required type="date" value={claim.claim_date} onChange={(e) => setClaim((v) => ({ ...v, claim_date: e.target.value }))} /></label>
            <label>Status<select value={claim.status} onChange={(e) => setClaim((v) => ({ ...v, status: e.target.value }))}>{["draft", "submitted", "info_requested", "approved", "paid", "denied", "closed"].map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}</select></label>
            <label>Claim number<input value={claim.claim_number} onChange={(e) => setClaim((v) => ({ ...v, claim_number: e.target.value }))} /></label>
            <label>Claim amount<input type="number" min="0" step="0.01" value={claim.amount} onChange={(e) => setClaim((v) => ({ ...v, amount: e.target.value }))} /></label>
            <label>Reimbursed<input type="number" min="0" step="0.01" value={claim.reimbursement_amount} onChange={(e) => setClaim((v) => ({ ...v, reimbursement_amount: e.target.value }))} /></label>
            <label>Vault evidence<select value={claim.vault_item_id} onChange={(e) => setClaim((v) => ({ ...v, vault_item_id: e.target.value }))}><option value="">No linked record</option>{data.vault_items.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</select></label>
            <label className="wide">Notes<textarea rows="3" value={claim.notes} onChange={(e) => setClaim((v) => ({ ...v, notes: e.target.value }))} /></label>
            <p className="wide hm-pet-form-note">HouseMind organizes claim progress and evidence. It does not submit claims to the insurer.</p>
            <footer className="wide"><button type="button" className="quiet" onClick={() => setClaimSheet(false)}>Cancel</button><button disabled={busy}>{busy ? "Saving…" : editingClaim ? "Update claim" : "Add claim"}</button></footer>
          </form>
        </div>
      )}
      {lostPet && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) closeLostCard(); }}>
          <section className="hm-pets-sheet hm-lost-pet-card" role="dialog" aria-modal="true" aria-label={`Lost pet card for ${lostPet.name}`}>
            <SheetHandle onClose={closeLostCard} label="Close lost pet card" />
            <header><div><span>Privacy-safe emergency card</span><h2>Help find {lostPet.name}</h2></div><button type="button" onClick={closeLostCard}>×</button></header>
            <div className="hm-lost-pet-preview">{lostPhotoUrl ? <img src={lostPhotoUrl} alt={lostPet.name} /> : <i>{petIcon(lostPet.species)}</i>}<h3>LOST {lostPet.species?.toUpperCase()}</h3><strong>{lostPet.name}</strong><p>{[lostPet.breed, lostPet.color_markings].filter(Boolean).join(" · ") || "Add breed and markings in the pet profile."}</p>{lostPet.microchip_number && <small>Microchipped · number available for identification</small>}</div>
            <label className="hm-lost-pet-photo"><span>{lostPhotoUrl ? "Change card photo" : "Add a real pet photo"}</span><input type="file" accept="image/*" onChange={chooseLostPhoto} /><small>Used only for this share or print. Maximum 10 MB.</small></label>
            <div className="hm-lost-pet-fields"><label>Contact name<input value={lostDetails.contact_name} onChange={(e) => setLostDetails((v) => ({ ...v, contact_name: e.target.value }))} /></label><label>Contact phone<input inputMode="tel" value={lostDetails.contact_phone} onChange={(e) => setLostDetails((v) => ({ ...v, contact_phone: e.target.value }))} /></label><label>Last seen date<input type="date" value={lostDetails.last_seen_date} onChange={(e) => setLostDetails((v) => ({ ...v, last_seen_date: e.target.value }))} /></label><label>Last seen location<input value={lostDetails.last_seen_location} onChange={(e) => setLostDetails((v) => ({ ...v, last_seen_location: e.target.value }))} /></label><label className="wide">Public notes<textarea rows="2" placeholder="Collar, temperament, safe approach instructions" value={lostDetails.notes} onChange={(e) => setLostDetails((v) => ({ ...v, notes: e.target.value }))} /></label></div>
            <p className="hm-pet-form-note">Only the information visible here is shared. Medical conditions, insurance, address, and unrelated household details are excluded.</p>
            {lostPhotoFile && <p className="hm-pet-form-note">The temporary photo is included in supported phone sharing and printing. A public link uses the pet’s saved profile photo.</p>}
            {lostPublicShare && <div className="hm-pet-public-link"><strong>Public link active for 7 days</strong><a href={lostPublicShare.url} target="_blank" rel="noreferrer">Open public page</a><button type="button" onClick={revokeLostPublicLink}>Revoke link</button></div>}
            <footer className="hm-lost-pet-actions"><button type="button" onClick={shareLostCard}>Share card</button><button type="button" onClick={createLostPublicLink} disabled={busy || !!lostPublicShare}>{busy ? "Creating…" : lostPublicShare ? "Public link active" : "Create public link"}</button><button type="button" onClick={() => window.print()}>Print</button><button type="button" onClick={() => petsApi.downloadReport(lostPet.id, "lost", lostDetails).catch((err) => reportAction(err.message, "error"))}>Download PDF</button></footer>
          </section>
        </div>
      )}
      {sharePet && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setSharePet(null); }}>
          <section className="hm-pets-sheet hm-pet-share-sheet" role="dialog" aria-modal="true" aria-label={`Share ${sharePet.name} with family`}>
            <SheetHandle onClose={() => setSharePet(null)} label="Close family sharing" />
            <header><div><span>Family sharing</span><h2>Share {sharePet.name}</h2></div><button type="button" onClick={() => setSharePet(null)}>×</button></header>
            <p>Share this pet only. Other pets and household records stay private.</p>
            <label>Family member<select value={shareTargetId} onChange={(e) => setShareTargetId(e.target.value)} disabled={!shareMembers.length}>{shareMembers.length ? shareMembers.map((member) => <option key={member.user_id} value={member.user_id}>{member.full_name || member.email}</option>) : <option value="">No Family members available</option>}</select></label>
            <fieldset><legend>Permission</legend><label><input type="radio" name="pet-share-permission" checked={sharePermission === "view"} onChange={() => setSharePermission("view")} /><span><strong>View only</strong><small>Can see the shared profile and care plan.</small></span></label><label><input type="radio" name="pet-share-permission" checked={sharePermission === "edit"} onChange={() => setSharePermission("edit")} /><span><strong>Help with care</strong><small>Can also mark today’s routines complete.</small></span></label></fieldset>
            <fieldset><legend>Include these private details</legend>{[["vet_contacts", "Veterinarian contacts"], ["medical_summary", "Medical summary and medication instructions"], ["insurance", "Insurance provider and policy number"], ["documents", "Linked document names"]].map(([key, label]) => <label key={key}><input type="checkbox" checked={shareScope[key]} onChange={(e) => setShareScope((value) => ({ ...value, [key]: e.target.checked }))} /><span><strong>{label}</strong></span></label>)}</fieldset>
            <small>Health-journal observations, insurance claims, costs, scanned document contents, and other household data are never included.</small>
            <footer><button type="button" className="quiet" onClick={() => setSharePet(null)}>Cancel</button><button type="button" disabled={busy || !shareTargetId} onClick={savePetShare}>{busy ? "Sharing…" : "Share pet"}</button></footer>
          </section>
        </div>
      )}
      {caregiverBrief && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setCaregiverBrief(null); }}>
          <section className="hm-pets-sheet hm-caregiver-brief" role="dialog" aria-modal="true" aria-label={`${caregiverBrief.pet.name} caregiver brief`}>
            <SheetHandle onClose={() => setCaregiverBrief(null)} label="Close caregiver brief" />
            <header><div><span>Caregiver handoff</span><h2>{petIcon(caregiverBrief.pet.species)} {caregiverBrief.pet.name}</h2></div><button type="button" onClick={() => setCaregiverBrief(null)}>×</button></header>
            <p className="hm-caregiver-intro">Everything a trusted caregiver needs, without exposing the rest of your household.</p>
            {[['Feeding', caregiverBrief.pet.feeding_notes], ['Allergies', caregiverBrief.pet.allergy_notes], ['Medical conditions', caregiverBrief.pet.medical_conditions], ['Medication', caregiverBrief.pet.medication_notes], ['Caregiver notes', caregiverBrief.pet.caregiver_notes]].filter(([, value]) => value).map(([label, value]) => <article key={label}><small>{label}</small><strong>{value}</strong></article>)}
            <div className="hm-caregiver-contacts"><a href={caregiverBrief.pet.primary_vet_phone ? `tel:${caregiverBrief.pet.primary_vet_phone}` : undefined}><small>Primary vet</small><strong>{caregiverBrief.pet.primary_vet_name || "Not recorded"}</strong><span>{caregiverBrief.pet.primary_vet_phone}</span></a><a href={caregiverBrief.pet.emergency_vet_phone ? `tel:${caregiverBrief.pet.emergency_vet_phone}` : undefined}><small>Emergency vet</small><strong>{caregiverBrief.pet.emergency_vet_name || "Not recorded"}</strong><span>{caregiverBrief.pet.emergency_vet_phone}</span></a></div>
            {!!caregiverBrief.routines.length && <section><h3>Daily routine</h3>{caregiverBrief.routines.map((row) => <p key={row.id}><b>{row.time_of_day}</b> · {row.title}{row.dosage ? ` · ${row.dosage}` : ""}</p>)}</section>}
            <small>{caregiverBrief.disclaimer}</small>
            <footer><button type="button" className="quiet" onClick={() => window.print()}>Print</button><button type="button" className="quiet" onClick={() => petsApi.downloadReport(caregiverBrief.pet.id, "caregiver").catch((err) => reportAction(err.message, "error"))}>Download PDF</button><button type="button" onClick={shareCaregiverBrief}>Share brief</button></footer>
          </section>
        </div>
      )}
      {vetVisitBrief && (
        <div className="hm-pets-sheet-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) setVetVisitBrief(null); }}>
          <section className="hm-pets-sheet hm-vet-visit-brief" role="dialog" aria-modal="true" aria-label={`${vetVisitBrief.pet.name} vet visit preparation`}>
            <SheetHandle onClose={() => setVetVisitBrief(null)} label="Close vet visit preparation" />
            <header><div><span>Vet visit preparation</span><h2>{petIcon(vetVisitBrief.pet.species)} {vetVisitBrief.pet.name}</h2></div><button type="button" onClick={() => setVetVisitBrief(null)}>×</button></header>
            <p className="hm-caregiver-intro">A factual, owner-entered summary to make the conversation easier. HouseMind does not diagnose or recommend treatment.</p>
            <div className="hm-vet-brief-facts">{[["Conditions", vetVisitBrief.pet.medical_conditions], ["Allergies", vetVisitBrief.pet.allergy_notes], ["Current medications", vetVisitBrief.pet.medication_notes], ["Current weight", vetVisitBrief.pet.weight_lbs ? `${vetVisitBrief.pet.weight_lbs} lb` : null]].map(([label, value]) => <article key={label}><small>{label}</small><strong>{value || "Not recorded"}</strong></article>)}</div>
            <section><h3>Last 30 days of observations</h3>{vetVisitBrief.observations.length ? vetVisitBrief.observations.map((row) => <p key={row.id}><b>{new Date(`${row.entry_date}T12:00:00`).toLocaleDateString()} · {row.entry_type.replaceAll("_", " ")}</b>{row.value_numeric != null ? ` · ${row.value_numeric} ${row.unit || ""}` : row.notes ? ` · ${row.notes}` : ""}</p>) : <p>No observations recorded in the last 30 days.</p>}</section>
            {!!vetVisitBrief.medication_routines.length && <section><h3>Medication routine</h3>{vetVisitBrief.medication_routines.map((row) => <p key={row.id}><b>{row.title}</b>{row.dosage ? ` · ${row.dosage}` : ""}{row.remaining_quantity != null ? ` · ${row.remaining_quantity} ${row.supply_unit || "doses"} left` : ""}</p>)}</section>}
            <section className="hm-vet-questions"><h3>Questions to consider asking</h3>{vetVisitBrief.prompt_questions.map((question) => <label key={question}><input type="checkbox" /> {question}</label>)}</section>
            <small>{vetVisitBrief.disclaimer}</small>
            <footer><button type="button" className="quiet" onClick={() => window.print()}>Print</button><button type="button" className="quiet" onClick={() => petsApi.downloadReport(vetVisitBrief.pet.id, "vet").catch((err) => reportAction(err.message, "error"))}>Download PDF</button><button type="button" onClick={shareVetVisitBrief}>Share brief</button></footer>
          </section>
        </div>
      )}
      {careSheet && (
        <div
          className="hm-pets-sheet-bg"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) { setCareSheet(false); setEditingCare(null); }
          }}
        >
          <form className="hm-pets-sheet hm-pets-form" onSubmit={addCare}>
            <SheetHandle
              onClose={() => { setCareSheet(false); setEditingCare(null); }}
              label="Close care form"
            />
            <header className="wide">
              <div>
                <span>Pet care</span>
                <h2>{editingCare ? "Update schedule" : "Add schedule"}</h2>
              </div>
              <button type="button" onClick={() => { setCareSheet(false); setEditingCare(null); }}>
                ×
              </button>
            </header>
            <label>
              Pet
              <select
                value={care.pet_id}
                required
                onChange={(e) =>
                  setCare((v) => ({ ...v, pet_id: e.target.value }))
                }
              >
                {data.pets.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Care item
              <input
                required
                list="hm-pet-care-title-suggestions"
                value={care.title}
                onChange={(e) =>
                  setCare((v) => ({ ...v, title: e.target.value }))
                }
              />
            </label>
            <label>
              Type
              <select
                value={care.care_type}
                onChange={(e) =>
                  setCare((v) => ({
                    ...v,
                    care_type: e.target.value,
                    title: "",
                  }))
                }
              >
                {Object.entries(CARE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Next due
              <input
                type="date"
                value={care.next_due}
                onChange={(e) =>
                  setCare((v) => ({ ...v, next_due: e.target.value }))
                }
              />
            </label>
            <label>
              Repeat
              <select
                value={care.frequency_days}
                onChange={(e) =>
                  setCare((v) => ({ ...v, frequency_days: e.target.value }))
                }
              >
                <option value="">Does not repeat</option>
                <option value="1">Daily</option>
                <option value="7">Weekly</option>
                <option value="30">Monthly</option>
                <option value="90">Every 3 months</option>
                <option value="180">Every 6 months</option>
                <option value="365">Yearly</option>
              </select>
            </label>
            <label>
              Estimated cost
              <input
                type="number"
                min="0"
                step="0.01"
                value={care.estimated_cost}
                onChange={(e) =>
                  setCare((v) => ({ ...v, estimated_cost: e.target.value }))
                }
              />
            </label>
            <label>
              Provider
              <select
                value={care.provider_id}
                onChange={(e) =>
                  setCare((v) => ({ ...v, provider_id: e.target.value }))
                }
              >
                <option value="">Not assigned</option>
                {data.providers.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Vault record
              <select
                value={care.vault_item_id}
                onChange={(e) =>
                  setCare((v) => ({ ...v, vault_item_id: e.target.value }))
                }
              >
                <option value="">No linked record</option>
                {data.documents.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Dosage / detail
              <input
                value={care.dosage}
                onChange={(e) =>
                  setCare((v) => ({ ...v, dosage: e.target.value }))
                }
              />
            </label>
            <label>
              Instructions
              <input
                value={care.instructions}
                onChange={(e) =>
                  setCare((v) => ({ ...v, instructions: e.target.value }))
                }
              />
            </label>
            <footer className="wide">
              <button
                type="button"
                className="quiet"
                onClick={() => { setCareSheet(false); setEditingCare(null); }}
              >
                Cancel
              </button>
              <button disabled={busy}>
                {busy ? "Saving…" : editingCare ? "Update schedule" : "Save schedule"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {complete && (
        <div
          className="hm-pets-sheet-bg"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setComplete(null);
          }}
        >
          <form className="hm-pets-sheet" onSubmit={finish}>
            <SheetHandle
              onClose={() => setComplete(null)}
              label="Close completed care form"
            />
            <header>
              <div>
                <span>Complete care</span>
                <h2>Save the finished record</h2>
              </div>
              <button type="button" onClick={() => setComplete(null)}>
                ×
              </button>
            </header>
            <label>
              Completed on
              <input
                required
                type="date"
                value={complete.care_date}
                onChange={(e) =>
                  setComplete((v) => ({ ...v, care_date: e.target.value }))
                }
              />
            </label>
            <label>
              Actual cost
              <input
                type="number"
                min="0"
                step="0.01"
                value={complete.cost}
                onChange={(e) =>
                  setComplete((v) => ({ ...v, cost: e.target.value }))
                }
              />
            </label>
            <label>
              Notes
              <textarea
                rows="3"
                value={complete.notes}
                onChange={(e) =>
                  setComplete((v) => ({ ...v, notes: e.target.value }))
                }
              />
            </label>
            <label>
              Follow up on
              <input type="date" value={complete.follow_up_date} onChange={(e) => setComplete((v) => ({ ...v, follow_up_date: e.target.value }))} />
            </label>
            <label>
              Follow-up instructions
              <textarea rows="2" value={complete.follow_up_notes} placeholder="Recheck, results, medication response…" onChange={(e) => setComplete((v) => ({ ...v, follow_up_notes: e.target.value }))} />
            </label>
            <footer>
              <button
                type="button"
                className="quiet"
                onClick={() => setComplete(null)}
              >
                Cancel
              </button>
              <button disabled={busy}>
                {busy ? "Saving…" : "Complete care"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {confirmAction && (
        <ConfirmModal
          tag={
            confirmAction.kind === "schedule"
              ? "⚠ Remove care schedule"
              : confirmAction.kind === "routine"
                ? "Remove daily routine"
              : confirmAction.kind === "claim"
                ? "Remove insurance claim"
              : confirmAction.kind === "health"
                ? "Remove health observation"
              : confirmAction.kind === "pet_food"
                ? "Remove pet food"
              : confirmAction.kind === "archive"
                ? "Archive pet"
                : "⚠ Permanently delete pet"
          }
          title={
            confirmAction.kind === "schedule"
              ? "Remove care schedule?"
              : confirmAction.kind === "routine"
                ? `Remove ${confirmAction.row.title}?`
              : confirmAction.kind === "claim"
                ? `Remove ${confirmAction.row.title}?`
              : confirmAction.kind === "health"
                ? "Remove this observation?"
              : confirmAction.kind === "pet_food"
                ? `Remove ${confirmAction.row.name}?`
              : confirmAction.kind === "archive"
                ? `Archive ${confirmAction.row.name}?`
                : `Delete ${confirmAction.row.name} permanently?`
          }
          message={
            confirmAction.kind === "schedule"
              ? `${confirmAction.row.title} will be removed from ${confirmAction.row.pet_name}'s active schedule. Completed history will be kept.`
              : confirmAction.kind === "routine"
                ? `This removes the routine from ${confirmAction.row.pet_name}'s Pet Today checklist. Existing completion history will be retained.`
              : confirmAction.kind === "claim"
                ? "This removes the claim record from HouseMind. It does not affect anything already submitted to your insurer."
              : confirmAction.kind === "health"
                ? `This removes the observation from ${confirmAction.row.pet_name}'s health journal. This cannot be undone.`
              : confirmAction.kind === "pet_food"
                ? "This removes the pet-food stock record. Feeding routines will stop tracking this supply, but existing Shopping items are not changed."
              : confirmAction.kind === "archive"
                ? "The profile and schedules will move to Archived Pets. Completed care history will be preserved, and you can restore the pet later."
                : "This permanently deletes the pet profile, schedules, and completed care history. This cannot be undone."
          }
          confirmLabel={
            confirmAction.kind === "archive"
              ? "Archive"
              : confirmAction.kind === "schedule"
                ? "Remove"
                : confirmAction.kind === "routine"
                  ? "Remove routine"
                : confirmAction.kind === "claim"
                  ? "Remove claim"
                : confirmAction.kind === "health"
                  ? "Remove observation"
                : confirmAction.kind === "pet_food"
                  ? "Remove pet food"
                : "Delete permanently"
          }
          onCancel={() => setConfirmAction(null)}
          onConfirm={performConfirmedAction}
          loading={busy}
        />
      )}
      {showSavingNotice && <SavingNotice message="Saving pet care changes..." />}
      {actionFeedback && <div className={`hm-pets-action-toast is-${actionFeedback.tone}`} role={actionFeedback.tone === "error" ? "alert" : "status"}>{actionFeedback.message}</div>}
    </main>
  );
}
